import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { crmDeepLink } from '@/lib/links.js';
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { fmtCents, fmtPct } from '@/components/money/format.js';

// REFUNDS DUE (TRACON-TRUST-LEDGER-SPEC §7.5): every return row where the
// client is owed money, with what the row says (amount, commission clawback,
// the transaction it came from) and the two ways it settles. Same shape as
// the sweep screen: one number to read, the amount typed into an empty field,
// refused with both figures on a mismatch. Settling by credit applies an
// existing deal credit sourced from the same return; settling by payment
// opens the A/P door with the client as payee (carrier_payments.payee_kind
// = 'client'). Nothing here computes a refund.

const selectCls = 'mt-1 h-11 w-full rounded-card border border-border bg-bg px-2 text-sm text-text';

function RefundFacts({ row }) {
  const t = row.transaction;
  return (
    <div className="min-w-0 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium text-text">{row.client || 'client not on file'}</span>
        {t ? <span className="text-xs text-text-dim">{t.type} {t.effective_date}</span> : <span className="text-xs text-danger">source transaction not found</span>}
        {t ? <a href={crmDeepLink('Deals', t.crm_deal_id)} target="_blank" rel="noreferrer" className="text-xs text-text-muted hover:underline">deal</a> : null}
      </div>
      <div className="mt-0.5 font-mono text-[11.5px] text-text-muted">
        owed to client {fmtCents(row.amount)} · commission clawback {fmtCents(row.commission_clawback)}
        {t ? <> · premium {fmtCents(t.premium_delta)} · tax {fmtCents(t.tax_delta)}{t.commission_rate != null ? <> · rate {fmtPct(t.commission_rate)} × {t.commission_share ?? 'share not stated'}</> : <span className="text-danger"> · no rate stated</span>}</> : null}
        {t && t.description ? <> · {t.description}</> : null}
        {t ? <> · source <span className="font-mono">{String(t.id).slice(0, 8)}</span></> : null}
      </div>
    </div>
  );
}

function SettleByCredit({ row, onSettled }) {
  // Both EMPTY. The credit's amount is printed beside the choice for reading,
  // never typed in (guard: tests/sweep-no-prefill.test.js).
  const [creditId, setCreditId] = useState('');
  const [amount, setAmount] = useState('');
  const [mismatch, setMismatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const usable = (row.credits || []).filter((c) => c.status !== 'applied' && c.status !== 'settled' && c.status !== 'void');
  const submit = async () => {
    setBusy(true);
    setMismatch(null);
    try {
      const r = await api.post(`/api/money/refunds/${row.id}/settle-credit`, { deal_credit_id: creditId, amount: Number(amount) });
      toast.success(`Refund of ${fmtCents(r.refund?.amount ?? row.amount)} settled by applying the credit.`);
      setCreditId('');
      setAmount('');
      await onSettled();
    } catch (e) {
      if (e.status === 409 && e.body?.code === 'REFUND_AMOUNT_MISMATCH') { setMismatch(e.body); return; }
      toast.error(e.body?.code || e.message || 'Could not settle the refund');
    } finally {
      setBusy(false);
    }
  };
  if (!usable.length) return <p className="text-xs text-text-muted">No open credit from this return to apply.</p>;
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="text-xs text-text-muted">
        Credit to apply
        <select value={creditId} onChange={(e) => setCreditId(e.target.value)} className={`${selectCls} w-72`}>
          <option value="">choose the credit</option>
          {usable.map((c) => <option key={c.id} value={c.id}>{fmtCents(c.amount)} · {c.status}{c.target_crm_deal_id ? ` · toward deal ${c.target_crm_deal_id}` : ''}{c.memo ? ` · ${c.memo}` : ''}</option>)}
        </select>
      </label>
      <label className="text-xs text-text-muted">
        Amount settled
        <Input type="number" step="0.01" inputMode="decimal" data-money-input="refund_amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-32" />
      </label>
      <Button variant="outline" onClick={submit} disabled={busy || !creditId || amount === ''}>{busy ? 'Recording...' : 'Apply credit'}</Button>
      {mismatch ? (
        <div className="w-full rounded-card border border-danger/40 bg-danger/5 p-3 text-sm">
          <div className="font-medium text-danger">Not recorded: the amount you typed is not the refund.</div>
          <div className="mt-1 font-mono text-xs text-text">Refund {fmtCents(mismatch.refund)} · Typed {fmtCents(mismatch.typed)} · Difference {fmtCents(mismatch.difference)}</div>
        </div>
      ) : null}
    </div>
  );
}

export default function MoneyRefunds() {
  const [rows, setRows] = useState(null);
  const [settled, setSettled] = useState([]);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      const [o, a] = await Promise.all([api.get('/api/money/refunds'), api.get('/api/money/refunds?all=1')]);
      setRows(o.refunds || []);
      setSettled((a.refunds || []).filter((r) => r.settled_at));
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load refunds');
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const total = (rows || []).reduce((acc, r) => acc + (Number(r.amount) || 0), 0);

  return (
    <div className="p-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Refunds due to clients</h2>
        <p className="mt-1 text-sm text-text-muted">
          Each row is a return the ledger recorded. Settle it by paying the client from the trust account, or by applying a credit that came from the same return.
        </p>
      </div>
      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}
      {rows === null && !error ? <p className="mt-4 text-sm text-text-muted">Loading&hellip;</p> : null}
      {rows ? (
        <>
          <div className="mt-4 rounded-card border border-border bg-surface p-4 text-sm text-text-muted">
            {rows.length} open refund{rows.length === 1 ? '' : 's'}, owed to clients: <span className="font-mono text-text">{fmtCents(total)}</span>
          </div>
          {rows.length === 0 ? <p className="mt-4 text-sm text-text-muted">Nothing owed to a client.</p> : (
            <ul className="mt-3 divide-y divide-border rounded-card border border-border bg-surface px-3">
              {rows.map((r) => (
                <li key={r.id} className="py-3">
                  <RefundFacts row={r} />
                  <div className="mt-2 flex flex-wrap items-start gap-4">
                    <SettleByCredit row={r} onSettled={refresh} />
                    <Link to={`/money/pay?refund=${r.id}`} className="inline-flex h-9 items-center rounded-card border border-border px-3 text-sm text-text hover:bg-bg">Settle by payment</Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <section className="mt-8 border-t border-border pt-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-text-dim">Settled</h3>
            {settled.length === 0 ? <p className="mt-2 text-sm text-text-muted">None yet.</p> : (
              <ul className="mt-2 divide-y divide-border rounded-card border border-border bg-surface px-3">
                {settled.map((r) => (
                  <li key={r.id} className="py-2 text-sm">
                    <span className="text-text">{r.client || 'client not on file'}</span>
                    <span className="ml-2 font-mono text-text">{fmtCents(r.amount)}</span>
                    <span className="ml-2 text-text-muted">{r.settled_by === 'applied_credit' ? 'credit applied' : 'paid'} {String(r.settled_at).slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
}
